import apiClient from './api';
import { LoginRequest, RegisterRequest, User, Organization, ApiResponse } from '@/types';

interface LoginResponse {
  user: User;
  tokens: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
  is_new_user?: boolean;
}

class AuthService {
  async login(credentials: LoginRequest): Promise<{ user: User; organization: Organization | null }> {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', credentials);
    if (response.data.success && response.data.data) {
      this.setTokens(response.data.data.tokens);
      const organization = await this.fetchOrganization();
      return {
        user: response.data.data.user,
        organization,
      };
    }
    throw new Error(response.data.error?.message || 'Login failed');
  }

  async register(data: RegisterRequest): Promise<{ user: User; organization: Organization | null }> {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/register', data);
    if (response.data.success && response.data.data) {
      this.setTokens(response.data.data.tokens);
      const organization = await this.fetchOrganization();
      return {
        user: response.data.data.user,
        organization,
      };
    }
    throw new Error(response.data.error?.message || 'Registration failed');
  }

  async getCurrentUser(): Promise<{ user: User; organization: Organization | null }> {
    const response = await apiClient.get<ApiResponse<User>>('/auth/me');
    if (response.data.success && response.data.data) {
      const organization = await this.fetchOrganization();
      return {
        user: response.data.data,
        organization,
      };
    }
    throw new Error('Failed to get current user');
  }

  async fetchOrganization(): Promise<Organization | null> {
    try {
      const response = await apiClient.get<ApiResponse<Organization>>('/settings/organization');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return null;
    } catch {
      return null;
    }
  }

  async logout(): Promise<void> {
    this.clearTokens();
  }

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post<ApiResponse<{ access_token: string; refresh_token: string }>>('/auth/refresh', {
      refresh_token: refreshToken,
    });

    if (response.data.success && response.data.data) {
      localStorage.setItem('access_token', response.data.data.access_token);
      localStorage.setItem('refresh_token', response.data.data.refresh_token);
      return response.data.data.access_token;
    }

    throw new Error('Token refresh failed');
  }

  private setTokens(tokens: { access_token: string; refresh_token: string }): void {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
  }

  private clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  async requestPasswordReset(email: string): Promise<void> {
    await apiClient.post('/auth/password/reset-request', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/password/reset', { token, new_password: newPassword });
  }

  async getGoogleAuthUrl(redirectUri: string): Promise<string> {
    const response = await apiClient.get<ApiResponse<{ url: string }>>(`/auth/google/client-id?redirect_uri=${encodeURIComponent(redirectUri)}`);
    if (response.data.success && response.data.data) {
      return response.data.data.url;
    }
    throw new Error('Google login is not available');
  }

  async googleLogin(code: string, redirectUri: string, referralCode?: string): Promise<{ user: User; organization: Organization | null; is_new_user: boolean }> {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/google', { code, redirect_uri: redirectUri, referral_code: referralCode });
    if (response.data.success && response.data.data) {
      this.setTokens(response.data.data.tokens);
      const organization = await this.fetchOrganization();
      return {
        user: response.data.data.user,
        organization,
        is_new_user: !!response.data.data.is_new_user,
      };
    }
    throw new Error(response.data.error?.message || 'Google login failed');
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }
}

export const authService = new AuthService();
export default authService;
